/**
 * Notification Service Module
 * 
 * Handles Firebase notification functionality including initialization,
 * permission requests, token registration, message handling, and notification sounds.
 */

import { initializeFirebase, setupMessageListener } from './FirebaseService';
import { toast } from 'react-toastify';
import * as Store from '../../../storeRedux';
// import { CLEAR_STORES } from '../../../Actions/StoreActions';
import { post } from '../Http';
import Config from '../../../Utility/Config';
// Simple audio object for playing sounds
let notificationSound = null;
let isAudioInitialized = false;

// Initialize audio system
const initializeAudio = () => {
  if (isAudioInitialized) return true;
  
  try {
    // Create audio element
    notificationSound = new Audio();
    
    // Set some properties
    notificationSound.volume = 1.0;
    notificationSound.preload = 'auto';
    
    // Load the sound file
    notificationSound.src = `https://mtpos-asset.s3.ap-south-1.amazonaws.com/sounds/notification.mp3?cache=${new Date().getTime()}`;
    
    // Try to load the audio file
    notificationSound.load();
    
    isAudioInitialized = true;
    return true;
  } catch (error) {
    console.warn('Error initializing audio system:', error);
    return false;
  }
};

// Token registration API function
const registerFcmToken = async (token) => {
  try {
    // Define the API endpoint path
    const url = `api/notifications/register-token`;
    const userDetails = Store.default.getState().LoginReducer.userEntryType;
    // console.log('userDetails', userDetails);
    // Prepare the request data
    const requestData = {
      ...userDetails,
      fcm_token: token,
      platform: 'web'
    };
    
    // Make API request using the project's HTTP post utility
    const response = await post(url, requestData);
    
    // Process the response
    const data = response.data;
    
    if (data.success) {
      console.log('FCM token registered successfully');
      return true;
    } else {
      console.error('Failed to register FCM token:', data.message);
      return false;
    }
  } catch (error) {
    // Error handling is already done in the post function's catch block
    console.error('Error registering FCM token:', error);
    return false;
  }
};

// Initialize notification service
export const initializeNotifications = async () => {
  // Check if the browser supports notifications
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return;
  }
  
  // Try to initialize the audio system with error handling
  try {
    // This will likely not work without user interaction, but we try anyway
    initializeAudio();
  } catch (error) {
    // Safely ignore any errors from audio initialization
    console.warn('Audio initialization failed:', error);
  }
  
  // Request permission and get token using the FirebaseService
  const token = await initializeFirebase();
  // console.log('firebase token', token);
  // Register token if available
  if (token) {
    await registerFcmToken(token);
  }
};

// Initialize audio on user interaction
export const initializeAudioOnInteraction = () => {
  // This function should be called from a click or touch handler
  try {
    // Initialize the audio
    initializeAudio();
    
    // Try to play a silent sound to unlock audio
    if (notificationSound) {
      notificationSound.volume = 0.01; // Very low volume
      notificationSound.play().catch(() => {
        // Ignore errors - this is just to unlock audio
      });
      notificationSound.volume = 1.0; // Reset volume
    }
    
    return true;
  } catch (error) {
    console.warn('Error initializing audio on interaction:', error);
    return false;
  }
};


// Play notification sound
const playNotificationSound = () => {
  try {
    // Initialize if not already done
    if (!isAudioInitialized) {
      initializeAudio();
    }
    
    // If we have a notification sound object
    if (notificationSound) {
      // Reset the sound to the beginning in case it's still playing
      notificationSound.currentTime = 0;
      
      // Try to play the sound
      // This will fail if there hasn't been user interaction
      notificationSound.play().catch(error => {
        console.warn('Error playing notification sound:', error);
        
        // Try creating a new Audio object as fallback
        try {
          const tempAudio = new Audio(`https://mtpos-asset.s3.ap-south-1.amazonaws.com/sounds/notification.mp3?cache=${new Date().getTime()}`);
          tempAudio.volume = 1.0;
          tempAudio.play().catch(() => {});
        } catch (e) {
          // Ignore errors in fallback
        }
      });
    } else {
      // Fallback to creating a new audio object
      const tempAudio = new Audio(`https://mtpos-asset.s3.ap-south-1.amazonaws.com/sounds/notification.mp3?cache=${new Date().getTime()}`);
      tempAudio.volume = 1.0;
      tempAudio.play().catch(() => {});
    }
  } catch (error) {
    console.warn('Error playing notification sound:', error);
  }
};

// Handle foreground messages
export const setupForegroundNotificationListener = (setNotification) => {
  // Try to initialize audio whenever this is called
  initializeAudio();
  
  return setupMessageListener(payload => {
    console.log('Received foreground message:', payload);
    
    const { notification, data } = payload;
    
    // // Check if this is a logout notification
    // if (data && data.action === 'logout') {
    //   // Clear store data
    //   Store.default.dispatch({
    //     type: CLEAR_STORES
    //   });
      
    //   // Play notification sound
    //   playNotificationSound();
      
    //   // Redirect to login after a short delay
    //   setTimeout(() => {
    //     window.location.href = window.location.origin + "/";
    //   }, 1000);
      
    //   return;
    // }
    
    // Play notification sound
    playNotificationSound();
    
    // Show toast notification with click handling for navigation
    /* toast.info(notification?.body || 'New notification', {
      autoClose: 5000,
      onClick: () => {
        // If URL is provided in data, navigate to it when toast is clicked
        if (data && data.url) {
          const urlToOpen = data.url.startsWith('/') ? data.url : `/${data.url}`;
          window.location.href = window.location.origin + urlToOpen;
        }
      }
    }); */
    
    // Update app state if needed
    if (setNotification && typeof setNotification === 'function') {
      setNotification({
        title: notification?.title || 'Notification',
        body: notification?.body || '',
        data: data || {}
      });
    }
    
    return payload;
  })
  .catch(err => {
    console.error('Error setting up notification listener:', err);
  });
};

/**
 * Create a notification payload with a URL to open when clicked
 * @param {string} title - Notification title
 * @param {string} body - Notification message body
 * @param {string} url - URL to open when notification is clicked (e.g., '/orders', '/products')
 * @param {Object} additionalData - Any additional data to include
 * @returns {Object} Notification payload object
 */
export const createUrlNotification = (title, body, url, additionalData = {}) => {
  return {
    notification: {
      title,
      body
    },
    data: {
      url, // URL to navigate to
      timestamp: Date.now().toString(),
      ...additionalData
    }
  };
};

const NotificationService = {
  initializeNotifications,
  setupForegroundNotificationListener,
  playNotificationSound,
  initializeAudio,
  initializeAudioOnInteraction,
  createUrlNotification
};

export default NotificationService;
